import { create } from 'zustand';
import type { Channel } from '@/lib/channels';

export type PlayerError = {
  message: string;
  type: 'cors' | 'network' | 'unknown';
};

export type LevelInfo = {
  height: number;
  bitrate: number;
  name: string;
};

type PlayerState = {
  currentChannel: Channel | null;
  isPlaying: boolean;
  error: PlayerError | null;
  /**
   * Increments on every retry/reset. Components depending on this
   * (useHls) destroy and recreate their underlying player.
   */
  reloadKey: number;

  // Playback timeline
  currentTime: number;
  duration: number;
  bufferedEnd: number;

  // Quality levels (HLS.js variant tracks)
  levels: LevelInfo[];
  currentLevel: number; // -1 = auto

  // Live state
  isLive: boolean;
  latency: number;

  // Lock: when true, navigation won't clear the channel
  playerLocked: boolean;
  togglePlayerLock: () => void;

  // Actions
  setCurrentChannel: (channel: Channel | null) => void;
  setIsPlaying: (playing: boolean) => void;
  setError: (error: PlayerError | null) => void;
  retry: () => void;
  triggerReload: () => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setBufferedEnd: (end: number) => void;
  setLevels: (levels: LevelInfo[]) => void;
  setCurrentLevel: (level: number) => void;
  setLive: (isLive: boolean) => void;
  setLatency: (latency: number) => void;
};

export const usePlayerStore = create<PlayerState>((set, get) => ({
  currentChannel: null,
  isPlaying: false,
  error: null,
  playerLocked: false,
  togglePlayerLock: () => set((s) => ({ playerLocked: !s.playerLocked })),
  reloadKey: 0,
  currentTime: 0,
  duration: 0,
  bufferedEnd: 0,
  levels: [],
  currentLevel: -1,
  isLive: false,
  latency: 0,

  setCurrentChannel: (channel) =>
    set({
      currentChannel: channel,
      error: null,
      isPlaying: !!channel,
      reloadKey: channel ? get().reloadKey + 1 : get().reloadKey,
      currentTime: 0,
      duration: 0,
      bufferedEnd: 0,
      levels: [],
      currentLevel: -1,
      isLive: false,
      latency: 0,
    }),

  setIsPlaying: (isPlaying) => set({ isPlaying }),

  setError: (error) => set({ error, isPlaying: false }),

  retry: () => {
    if (!get().currentChannel) return;
    set({ error: null, isPlaying: true, reloadKey: get().reloadKey + 1 });
  },

  triggerReload: () => {
    if (!get().currentChannel) return;
    set({ error: null, isPlaying: true, reloadKey: get().reloadKey + 1 });
  },

  setCurrentTime: (currentTime) => set({ currentTime }),
  setDuration: (duration) => set({ duration }),
  setBufferedEnd: (bufferedEnd) => set({ bufferedEnd }),
  setLevels: (levels) => set({ levels }),
  setCurrentLevel: (currentLevel) => set({ currentLevel }),
  setLive: (isLive) => set({ isLive }),
  setLatency: (latency) => set({ latency }),
}));
